<script setup>
import { defineModel, computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { picoSearch } from '@scmmishra/pico-search';
import Icon from 'next/icon/Icon.vue';
import Button from 'next/button/Button.vue';
import DropdownContainer from 'next/dropdown-menu/base/DropdownContainer.vue';
import DropdownBody from 'next/dropdown-menu/base/DropdownBody.vue';

const { options, maxChips, placeholder } = defineProps({
  options: {
    type: Array,
    required: true,
  },
  maxChips: {
    type: Number,
    default: 1,
  },
  placeholder: {
    type: String,
    default: '',
  },
});

const { t } = useI18n();

const selected = defineModel({
  type: Array,
  required: true,
});

const searchTerm = ref('');

const flatOptions = computed(() => {
  const result = [];
  const flatten = (items, depth = 0) => {
    for (const item of items) {
      result.push({ ...item, depth });
      if (item.additionalInfo?.children?.length) {
        flatten(item.additionalInfo.children, depth + 1);
      }
    }
  };
  flatten(options);
  return result;
});

const searchResults = computed(() => {
  if (!searchTerm.value) return flatOptions.value;
  return picoSearch(flatOptions.value, searchTerm.value, ['name']);
});

const selectedIds = computed(() => {
  if (!selected.value?.length) return [];
  // selected.value is already an array of IDs
  return selected.value;
});

const hasItems = computed(() => selected.value?.length > 0);

const selectedItems = computed(() => {
  if (!hasItems.value) return [];
  return flatOptions.value.filter(o => selectedIds.value.includes(o.id));
});

const selectedVisibleItems = computed(() => {
  if (!hasItems.value) return [];
  if (selectedItems.value.length === maxChips + 1) return selectedItems.value;
  return selectedItems.value.slice(0, maxChips);
});

const remainingItems = computed(() => {
  if (!hasItems.value) return [];
  if (selectedItems.value.length === maxChips + 1) return [];
  return selectedItems.value.slice(maxChips);
});

const remainingTooltip = computed(() =>
  remainingItems.value.map(i => i.name).join(', ')
);

const toggleOption = option => {
  const idToToggle = option.id;
  const subIds = option.additionalInfo?.subIds || [];

  if (selectedIds.value.includes(idToToggle)) {
    const idsToRemove = new Set([idToToggle, ...subIds]);
    selected.value = selected.value.filter(id => !idsToRemove.has(id));
  } else {
    const idsToAdd = [idToToggle, ...subIds].filter(
      id => !selectedIds.value.includes(id)
    );
    selected.value = [...(selected.value ?? []), ...idsToAdd];
  }
};
</script>

<template>
  <DropdownContainer>
    <template #trigger="{ toggle }">
      <button
        v-if="hasItems"
        class="bg-n-alpha-2 py-2 rounded-lg h-8 flex items-center px-0"
        @click="toggle"
      >
        <div
          v-for="item in selectedVisibleItems"
          :key="item.id"
          class="px-3 border-r rtl:border-l rtl:border-r-0 border-n-weak text-n-slate-12 text-sm flex gap-2 items-center max-w-[100px]"
        >
          <Icon v-if="item.icon" :icon="item.icon" class="flex-shrink-0" />
          <span class="truncate">{{ item.name }}</span>
        </div>
        <div
          v-if="remainingItems.length > 0"
          v-tooltip.top="remainingTooltip"
          class="px-3 border-r rtl:border-l rtl:border-r-0 border-n-weak text-n-slate-12 text-sm flex gap-2 items-center max-w-[100px]"
        >
          <span class="truncate">{{
            t('COMBOBOX.MORE', { count: remainingItems.length })
          }}</span>
        </div>
        <div class="flex items-center border-none px-3 gap-2">
          <Icon icon="i-lucide-plus" />
        </div>
      </button>
      <Button v-else sm slate faded @click="toggle">
        <template #icon>
          <Icon icon="i-lucide-plus" class="text-n-slate-11" />
        </template>
        <span class="text-n-slate-11">{{
          placeholder || t('COMBOBOX.PLACEHOLDER')
        }}</span>
      </Button>
    </template>

    <DropdownBody class="top-0 w-max min-w-64 z-50" strong>
      <!-- Search -->
      <div class="relative px-1 pb-1">
        <Icon
          icon="i-lucide-search"
          class="absolute size-4 left-3 top-1/2 -translate-y-1/2 text-n-slate-10"
        />
        <input
          v-model="searchTerm"
          autofocus
          class="w-full pl-8 pr-3 py-1.5 text-sm text-n-slate-12 bg-n-alpha-1 rounded-lg placeholder:text-n-slate-10 outline-none"
          :placeholder="t('COMBOBOX.SEARCH_PLACEHOLDER')"
        />
      </div>

      <!-- Options list -->
      <ul class="max-h-72 overflow-y-auto grid gap-0.5 list-none reset-base">
        <li v-for="option in searchResults" :key="option.id">
          <button
            type="button"
            class="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-n-alpha-2 text-sm text-n-slate-12 text-left"
            :style="
              option.depth
                ? { paddingLeft: `${0.5 + option.depth * 1}rem` }
                : {}
            "
            @click="toggleOption(option)"
          >
            <Icon
              v-if="option.icon"
              :icon="option.icon"
              class="flex-shrink-0 size-3.5"
            />
            <span class="flex-1 whitespace-nowrap">{{ option.name }}</span>
            <!-- Checkbox -->
            <span
              class="flex-shrink-0 size-4 rounded border flex items-center justify-center transition-colors"
              :class="
                selectedIds.includes(option.id)
                  ? 'bg-n-blue-text border-n-blue-text'
                  : 'border-n-strong bg-transparent'
              "
            >
              <Icon
                v-if="selectedIds.includes(option.id)"
                icon="i-lucide-check"
                class="size-3 text-n-background"
              />
            </span>
          </button>
        </li>
        <li
          v-if="searchResults.length === 0"
          class="px-2 py-1.5 text-sm text-n-slate-10 text-center"
        >
          {{
            searchTerm
              ? t('COMBOBOX.EMPTY_SEARCH_RESULTS', { searchTerm })
              : t('COMBOBOX.EMPTY_STATE')
          }}
        </li>
      </ul>
    </DropdownBody>
  </DropdownContainer>
</template>
